import React, { useLayoutEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const SubmissionInsights = () => {
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = location.pathname === "/article"
    ? "article"
    : location.pathname === "/shortlisted"
      ? "shortlisted"
      : location.pathname === "/finalists"
        ? "finalists"
        : "submission-insights";

  const handleTabClick = (tab) => {
    if (tab === "article") {
      navigate("/article");
    } else if (tab === "shortlisted") {
      navigate("/shortlisted");
    } else if (tab === "finalists") {
      navigate("/finalists");
    } else {
      navigate("/submission-insights");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#051005] to-black text-gray-200 py-12 sm:py-16 md:py-24 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">

        {/* Tabs (Button Style Toggle) */}
        <div className="pt-12 sm:pt-0">
          {/* Tabs */}

          <div className="flex justify-center mb-2 w-full">
            <div className="flex flex-col sm:flex-row gap-2 bg-white/5 p-2 sm:p-1 rounded-2xl sm:rounded-full border border-white/10 w-[92%] sm:w-fit">
              <button
                onClick={() => handleTabClick("submission-insights")}
                className={`flex-1 whitespace-nowrap px-4 sm:px-6 py-2.5 sm:py-2 text-sm font-semibold rounded-full transition-all duration-200 ${activeTab === "submission-insights"
                  ? "bg-green-500 text-black shadow-md"
                  : "text-gray-300 hover:text-white hover:bg-white/10"
                  }`}
              >
                Submission Insights
              </button>

              <button
                onClick={() => handleTabClick("article")}
                className={`flex-1 whitespace-nowrap px-4 sm:px-6 py-2.5 sm:py-2 text-sm font-semibold rounded-full transition-all duration-200 ${activeTab === "article"
                  ? "bg-green-500 text-black shadow-md"
                  : "text-gray-300 hover:text-white hover:bg-white/10"
                  }`}
              >
                Evaluation Insights And Result
              </button>

              <button
                onClick={() => handleTabClick("shortlisted")}
                className={`flex-1 whitespace-nowrap px-4 sm:px-6 py-2.5 sm:py-2 text-sm font-semibold rounded-full transition-all duration-200 ${activeTab === "shortlisted"
                  ? "bg-green-500 text-black shadow-md"
                  : "text-gray-300 hover:text-white hover:bg-white/10"
                  }`}
              >
                Shortlisted  Use Cases
              </button>

              <button
                onClick={() => handleTabClick("finalists")}
                className={`flex-1 whitespace-nowrap px-4 sm:px-6 py-2.5 sm:py-2 text-sm font-semibold rounded-full transition-all duration-200 ${activeTab === "finalists"
                  ? "bg-green-500 text-black shadow-md"
                  : "text-gray-300 hover:text-white hover:bg-white/10"
                  }`}
              >
                Meet the Finalists
              </button>
            </div>
          </div>
        </div>

        {/* Header */}
        <header className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-4 sm:mb-6 bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent tracking-tight leading-tight">
            Udbhav 2.0: Submission Insights
            <span className="block mt-2 text-xl sm:text-2xl md:text-3xl">542 Ideas, Countless Possibilities, and a Growing AI Movement at ONGC.</span>
          </h1>
        </header>

        {/* Main Content */}
        <article className="space-y-8 sm:space-y-12">
          <section>
            <p className="text-base sm:text-lg leading-relaxed text-gray-300">
              When we opened the gates for Udbhav 2.0, we weren't chasing numbers.
              <span className="text-green-400 font-semibold">
                {" "}We were chasing intent!!
              </span>
            </p>

            <p className="text-base sm:text-lg leading-relaxed mt-4 sm:mt-6 text-gray-300">
              We wanted to see whether the spark ignited during Udbhav 1.0 had sustained itself, whether curiosity had turned into confidence, and whether ideas had begun to mature into sharper, more actionable thinking.
              <span className="block text-green-400 font-semibold mt-2">
                What we witnessed over the past few weeks answered that question loud and clear.
              </span>
            </p>
          </section>

          <section className="py-6 sm:py-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold mb-4 sm:mb-6 leading-tight tracking-tight bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent text-center">
              Almost 500 New Ideas. From Across ONGC. Once Again.
            </h2>

            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-4 sm:mb-6">
              As the idea submission phase of <span className="font-semibold text-white">Udbhav 2.0</span> comes to a close, <span className="font-semibold text-green-400">542 use-case ideas</span> have poured in from ONGCians across the country — spanning <span className="text-white font-medium">offices, field locations, institutes, and operational sites</span>.
            </p>

            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6 sm:mb-8">
              From <span className="font-medium text-white">Mumbai, Ahmedabad, Rajahmundry, Delhi, Mehsana, Dehradun, Vadodara, Agartala</span> and many more locations, teams came together with a shared intent:
            </p>

            <blockquote className="relative my-6 sm:my-10 px-4 sm:px-6 py-4 sm:py-5 rounded-xl bg-gradient-to-r from-green-900/30 to-emerald-900/20 border border-green-500/30">
              <span className="absolute -top-3 left-4 sm:left-6 bg-black px-3 text-xs uppercase tracking-wider text-green-400">
                Shared Question
              </span>
              <p className="text-center text-base sm:text-lg italic text-white font-medium leading-relaxed pt-2">
                How can we do what we do — <span className="text-red-400 font-semibold">better, smarter, safer,</span> and more efficiently?
              </p>
            </blockquote>

            <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
              If <span className="font-semibold text-white">Udbhav 1.0</span> proved that AI curiosity exists at ONGC, <span className="font-semibold text-red-400">Udbhav 2.0 proves</span> that curiosity is now translating into <span className="font-semibold text-white">structured thinking</span>.
            </p>
          </section>

          <section className="py-6 sm:py-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold mb-4 sm:mb-6 bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent text-center">
              Not Just More Ideas. Better Ones.
            </h2>

            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6 sm:mb-8">
              This time, something felt different. The ideas submitted in <span className="font-semibold text-white">Udbhav 2.0</span> were:
            </p>

            <ul className="space-y-3 text-base sm:text-lg text-gray-300 mb-6 sm:mb-10">
              <li className="flex gap-3">
                <span className="text-green-400 font-semibold min-w-fit">•</span>
                <span>More <span className="font-semibold text-green-400">problem-focused</span></span>
              </li>
              <li className="flex gap-3">
                <span className="text-green-400 font-semibold min-w-fit">•</span>
                <span>Better articulated around <span className="font-semibold text-green-400">current process gaps</span></span>
              </li>
              <li className="flex gap-3">
                <span className="text-green-400 font-semibold min-w-fit">•</span>
                <span>Clearer about where AI fits — <span className="italic text-gray-400">and where it doesn't</span></span>
              </li>
            </ul>

            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6 sm:mb-8">
              Machine Learning, NLP, LLMs, Computer Vision, forecasting, analytics — AI wasn't treated as a buzzword. It was treated as a <span className="text-white font-medium">practical tool</span>, applied thoughtfully to real <span className="text-white font-medium">operational, safety, maintenance, production, finance, HR, and logistics</span> challenges.
            </p>

            <Card className="bg-gradient-to-r from-red-900/30 to-black/40 border border-red-500/40 p-4 sm:p-5 text-center max-w-3xl mx-auto">
              <p className="text-sm sm:text-base font-semibold text-red-400 leading-relaxed">
                These were not "AI for the sake of AI" ideas.
                <br />
                These were{" "}
                <span className="text-white">
                  ONGC problems, described by ONGCians
                </span>
                , with AI as an enabler.
              </p>
            </Card>
          </section>

          <section className="py-6 sm:py-8">
            <h2 className="text-center text-xl sm:text-2xl md:text-3xl font-extrabold mb-4 sm:mb-6 bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent">
              The Real Story Isn't the Count
            </h2>

            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6 sm:mb-8">
              The Numbers Tell a Story — and It's a Powerful One:
            </p>

            <ul className="text-base sm:text-lg text-gray-300 leading-relaxed space-y-3 mb-6 sm:mb-10">
              <li className="flex gap-3">
                <span className="text-green-400 font-semibold min-w-fit">•</span>
                <span><span className="font-semibold text-white">373 teams</span> came together to submit a total of <span className="font-semibold text-green-400">542 use case ideas</span></span>
              </li>
              <li className="flex gap-3">
                <span className="text-green-400 font-semibold min-w-fit">•</span>
                <span><span className="font-semibold text-green-400">54%</span> of these ideas came from <span className="font-semibold text-white">first-time participants</span>, highlighting the expanding reach of Udbhav beyond last season's cohort</span>
              </li>
            </ul>

            <div className="mb-8 sm:mb-12 bg-gradient-to-b from-white/5 to-transparent rounded-xl p-4 sm:p-6 border border-white/10">
              <div className="aspect-video bg-gradient-to-br from-green-900/20 to-black/40 rounded-lg flex items-center justify-center border border-white/10">
                <img
                  src="/images/graph.jpg"
                  alt="AI technologies and idea distribution across locations"
                  className="w-full rounded-xl border border-white/10 shadow-lg"
                />
              </div>
              <p className="mt-3 text-sm text-center text-gray-400">
                Idea distribution by AI technology and location
              </p>
            </div>

            <div className="mb-8 sm:mb-12 bg-gradient-to-b from-white/5 to-transparent rounded-xl p-4 sm:p-6 border border-white/10">
              <div className="aspect-video bg-gradient-to-br from-green-900/20 to-black/40 rounded-lg flex items-center justify-center border border-white/10">
                <img
                  src="/images/graph2.jpg"
                  alt="Function-wise idea distribution"
                  className="w-full rounded-xl border border-white/10 shadow-lg"
                />
              </div>
              <p className="mt-3 text-sm text-center text-gray-400">
                Function-wise distribution of submitted ideas
              </p>
            </div>

            <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 shadow-xl">
              <p className="text-center text-base sm:text-lg text-gray-200 leading-relaxed mb-4">
                We could talk a lot more about <span className="font-semibold text-white">numbers and graphs</span> (and yes — those insights will come soon <span className="text-yellow-400">🙂</span>).
              </p>

              <p className="text-center text-base sm:text-lg text-gray-200 leading-relaxed">
                But before the <span className="text-white font-medium">charts, dashboards, and shortlisting</span>, there's something <span className="text-red-400 font-semibold">more important</span> to acknowledge.
              </p>
            </div>
          </section>

          <section className="py-6 sm:py-8">
            <h2 className="text-center text-xl sm:text-2xl md:text-3xl font-extrabold mb-6 sm:mb-8 bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent">
              The People Behind the Ideas
            </h2>

            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6 sm:mb-8 text-center">
              Behind every submission is:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-10">
              {[
                "Someone who paused their daily work and thought deeply",
                "Someone who questioned an existing process",
                "Someone who believed their idea deserved a hearing",
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4"
                >
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-400 flex-shrink-0 mt-1" />
                  <span className="text-base sm:text-lg text-gray-300">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-center text-sm sm:text-base font-semibold text-white">
              That effort matters. And Udbhav exists precisely to honour that.
            </p>
          </section>

          <section className="py-6 sm:py-8">
            <h2 className="text-center text-xl sm:text-2xl md:text-3xl font-extrabold mb-4 sm:mb-6 bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent">
              The Last-Minute Surge
            </h2>

            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-4 sm:mb-6">
              Because we are engineers too, we know how this works.
            </p>

            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-4 sm:mb-6">
              And true to form, the final hours saw a familiar surge — teams racing against time, submissions coming in just before the deadline, ideas being polished till the very last minute.
            </p>

            <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
              Recognizing this reality, and staying true to the spirit of inclusion, <span className="text-white font-medium">a short grace period was provided</span> — not to dilute standards, but to ensure that no genuine idea was left unheard due to circumstance.
            </p>
          </section>

          <section className="py-6 sm:py-8">
            <h2 className="text-center text-xl sm:text-2xl md:text-3xl font-extrabold mb-6 sm:mb-8 bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent">
              What Happens Next: Where Ideas Start to Take Shape
            </h2>

            <div className="max-w-6xl mx-auto text-gray-300 space-y-4 sm:space-y-6">
              <p className="text-base sm:text-lg leading-relaxed">
                With idea submission now complete,{" "}
                <span className="font-semibold text-white">
                  Udbhav 2.0 moves into its most important phase
                </span>.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
                <div>
                  <p className="text-base sm:text-lg mb-4 font-semibold text-white">
                    Over the coming weeks:
                  </p>
                  <ul className="space-y-2 text-base sm:text-lg">
                    {[
                      "Every team will be heard",
                      "Short conversations will replace long forms",
                      "Ideas will be understood in context, not just on paper",
                    ].map((item, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="text-green-400 font-semibold">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="text-base sm:text-lg mb-4 font-semibold text-white">
                    This is where:
                  </p>
                  <ul className="space-y-2 text-base sm:text-lg">
                    {[
                      "Problems get sharper",
                      "Solutions get refined",
                      "Teams get strengthened",
                    ].map((item, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="text-green-400 font-semibold">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                    <li className="flex gap-3">
                      <span className="text-green-400 font-semibold">•</span>
                      <span>
                        Ideas begin their journey from{" "}
                        <span className="italic">possibility</span> to{" "}
                        <span className="italic">prototype</span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <p className="text-base sm:text-lg leading-relaxed mt-4 sm:mt-6">
                Selected teams will be mentored, supported, trained, and challenged not to
                impress, but to{" "}
                <span className="font-semibold text-white">
                  build something real
                </span>.
              </p>
            </div>
          </section>

          <section className="py-6 sm:py-8">
            <h2 className="text-center text-xl sm:text-2xl md:text-3xl font-extrabold mb-6 sm:mb-8 bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent">
              Udbhav Is No Longer an Experiment
            </h2>

            <div className="space-y-4">
              <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
                <span className="font-semibold text-white">Udbhav 1.0</span> proved that ONGC has ideas. <span className="font-semibold text-red-400">Udbhav 2.0</span> proves that ONGC is building capability.
                This is no longer about <span className="italic text-gray-400">"trying AI"</span>. This is about <span className="font-semibold text-white">embedding innovation into how we think and work</span>.
              </p>

              <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
                And the most exciting part? <span className="font-semibold text-white">This journey is being led not by a few — but by many.</span>
              </p>
            </div>
          </section>

          <section className="py-6 sm:py-8">
            <h2 className="text-center text-xl sm:text-2xl md:text-3xl font-extrabold mb-6 sm:mb-8 bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent">
              To Every ONGCian Who Submitted an Idea: Thank You
            </h2>

            <div className="space-y-4">
              <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
                Whether your idea moves forward to implementation or not, it has already contributed to something larger. A culture where <span className="font-semibold text-white">thinking differently</span> is encouraged. Where <span className="font-semibold text-white">ideas are welcomed</span>. Where <span className="font-semibold text-white">innovation is participative</span>, not abstract.
              </p>

              <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
                <span className="font-semibold text-red-400">Udbhav 2.0</span> is underway — and this is just the beginning.
              </p>

              <p className="text-center text-sm sm:text-base font-semibold text-white">
                The journey from ideas to impact <span className="italic text-gray-400">starts now</span>. 🚀
              </p>
            </div>
          </section>
        </article>
      </div>
    </div>
  );
};

export default SubmissionInsights;